
import { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { FilmFormValues } from "@/types/inventory";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "@/hooks/useTranslation";

interface FilmDetailsFieldsProps {
  form: UseFormReturn<FilmFormValues>;
}

export function FilmDetailsFields({ form }: FilmDetailsFieldsProps) {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const [isoError, setIsoError] = useState<string | null>(null);
  const [quantityError, setQuantityError] = useState<string | null>(null);
  
  const validateIso = (value: number | string) => {
    const numValue = Number(value);
    if (isNaN(numValue)) {
      setIsoError("ISO must be a number");
      return false;
    }
    if (numValue < 1) {
      setIsoError("ISO must be at least 1");
      return false;
    }
    if (numValue > 12800) {
      setIsoError("ISO cannot exceed 12800");
      return false;
    }
    setIsoError(null);
    return true;
  };
  
  const validateQuantity = (value: number | string) => {
    const numValue = Number(value);
    if (isNaN(numValue)) {
      setQuantityError("Quantity must be a number");
      return false;
    }
    if (numValue < 1) {
      setQuantityError("Quantity must be at least 1");
      return false;
    }
    if (numValue > 1000) {
      setQuantityError("Quantity cannot exceed 1000");
      return false;
    }
    setQuantityError(null);
    return true;
  };
  
  return (
    <div className="space-y-4">
      {/* In mobile view, place ISO and quantity on the same line */}
      <div className="grid grid-cols-2 gap-2">
        <FormField
          control={form.control}
          name="iso"
          render={({ field }) => (
            <FormItem>
              <FormLabel>ISO</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min={1}
                  max={12800}
                  {...field}
                  onChange={(e) => {
                    const value = e.target.value;
                    const isValid = validateIso(value);
                    if (isValid) {
                      field.onChange(Number(value));
                    }
                  }}
                  onBlur={() => validateIso(field.value)}
                  className={`${isMobile ? "text-base" : ""} ${isoError ? "border-destructive" : ""}`} 
                />
              </FormControl>
              {isoError && (
                <p className="text-xs font-medium text-destructive">{isoError}</p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="quantity"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('quantity')}</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min={1}
                  max={1000}
                  {...field}
                  onChange={(e) => {
                    const value = e.target.value;
                    const isValid = validateQuantity(value);
                    if (isValid) {
                      field.onChange(Number(value));
                    }
                  }}
                  onBlur={() => validateQuantity(field.value)}
                  className={`${isMobile ? "text-base" : ""} ${quantityError ? "border-destructive" : ""}`} 
                />
              </FormControl>
              {quantityError && (
                <p className="text-xs font-medium text-destructive">{quantityError}</p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      
      <FormField
        control={form.control}
        name="film_type"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('film_type')}</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className={isMobile ? "text-base" : ""}>
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent position={isMobile ? "popper" : "item-aligned"} className={isMobile ? "w-[calc(100vw-2rem)]" : ""}>
                <SelectItem value="35mm">35mm</SelectItem>
                <SelectItem value="120">120</SelectItem>
                <SelectItem value="220">220</SelectItem>
                <SelectItem value="large_format">{t('large_format')}</SelectItem>
                <SelectItem value="instant_film">{t('instant_film')}</SelectItem>
              </SelectContent>
            </Select>
            <FormDescription className="text-xs text-muted-foreground">
              Select the type of film you are adding to inventory
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
